import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reportCreatorI18n } from '../i18n';

const normalizeDataType = (dataType) => String(dataType || '').toLowerCase();

const ConfigPanel = ({
  DesignSystemComponents,
  selectedChip,
  onUpdateChip,
  onRequestFieldValues,
  operatorCatalog,
  className = '',
  onClose,
}) => {
  const { Input, Dropdown, ComboBox } = DesignSystemComponents ?? {};
  const { t } = useTranslation(undefined, { i18n: reportCreatorI18n });
  const [valueOptions, setValueOptions] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [valueSearch, setValueSearch] = useState('');
  const [valueError, setValueError] = useState('');

  const defaultFilterOptions = useMemo(
    () => [
      { label: t('rc_config_filter_equal'), value: 'eq' },
      { label: t('rc_config_filter_not_equal'), value: 'neq' },
    ],
    [t]
  );

  const sortOptions = useMemo(
    () => [
      { label: t('rc_config_sort_asc'), value: 'asc' },
      { label: t('rc_config_sort_desc'), value: 'desc' },
    ],
    [t]
  );

  const aggregationOptions = useMemo(() => {
    if (!selectedChip) return [];
    const allowed = Array.isArray(selectedChip.supportedAggregates)
      ? selectedChip.supportedAggregates
      : [];
    return allowed.map((item) => ({
      label: item.toUpperCase(),
      value: item,
    }));
  }, [selectedChip]);

  const operatorMap = useMemo(
    () =>
      new Map(
        (Array.isArray(operatorCatalog) ? operatorCatalog : [])
          .filter((item) => item?.key)
          .map((item) => [String(item.key).toLowerCase(), item])
      ),
    [operatorCatalog]
  );

  const filterOptions = useMemo(() => {
    if (!selectedChip) return [];
    const allowed = Array.isArray(selectedChip.supportedOperators)
      ? selectedChip.supportedOperators
      : [];
    if (!allowed.length) return defaultFilterOptions;
    return allowed.map((item) => ({
      label: operatorMap.get(item)?.label || item,
      value: item,
    }));
  }, [selectedChip, operatorMap, defaultFilterOptions]);

  const selectedOperatorMeta = useMemo(() => {
    if (!selectedChip?.operator) return null;
    return operatorMap.get(String(selectedChip.operator).toLowerCase()) || null;
  }, [selectedChip, operatorMap]);

  useEffect(() => {
    let mounted = true;

    const loadValues = async () => {
      if (
        !selectedChip ||
        selectedChip.zone !== 'filters' ||
        !selectedChip.supportsValueLookup
      ) {
        setValueOptions([]);
        setValueError('');
        return;
      }

      try {
        setLoadingValues(true);
        setValueError('');
        const items = await onRequestFieldValues(
          selectedChip.fieldKey,
          valueSearch || '',
          50
        );
        if (!mounted) return;
        setValueOptions(items);
      } catch (error) {
        if (!mounted) return;
        setValueOptions([]);
        setValueError(
          error.customMessage ||
            error.message ||
            t('rc_config_values_error')
        );
      } finally {
        if (mounted) setLoadingValues(false);
      }
    };

    loadValues();

    return () => {
      mounted = false;
    };
  }, [selectedChip, onRequestFieldValues, valueSearch, t]);

  if (!selectedChip) {
    return (
      <aside className={`report-creator-panel ${className}`.trim()}>
        <h3 className='fw-semibold mb-2'>{t('rc_config_title')}</h3>
        <p className='caption color-gray-600 mb-0'>
          {t('rc_config_empty')}
        </p>
      </aside>
    );
  }

  const selectedAggregation = aggregationOptions.find(
    (item) => item.value === selectedChip.aggregation
  );
  const selectedSort = sortOptions.find(
    (item) => item.value === selectedChip.direction
  );
  const selectedFilter = filterOptions.find(
    (item) => item.value === selectedChip.operator
  );

  const currentFirstValue = selectedChip.values?.[0] || '';
  const currentSecondValue = selectedChip.values?.[1] || '';
  const selectedValueOption = valueOptions.find(
    (item) => item.value === currentFirstValue
  );

  const filterInputType = (() => {
    const dataType = normalizeDataType(selectedChip.dataType);
    if (
      dataType === 'number' ||
      dataType === 'decimal' ||
      dataType === 'integer'
    ) {
      return 'number';
    }
    if (dataType === 'date' || dataType === 'datetime') {
      return 'date';
    }
    return 'text';
  })();

  return (
    <aside className={`report-creator-panel ${className}`.trim()}>
      <div className='d-flex align-items-start justify-content-between gap-2'>
        <div>
          <h3 className='fw-semibold mb-1'>{t('rc_config_title')}</h3>
          <p className='caption color-gray-600 mb-3'>
            {selectedChip.entity} · {selectedChip.label}
          </p>
        </div>
        {onClose && (
          <button
            type='button'
            className='builder-chip-action-btn'
            aria-label={t('rc_config_close_aria')}
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>

      {Input ? (
        <Input
          placeholder={t('rc_config_alias_placeholder')}
          value={selectedChip.alias || ''}
          onChange={(event) => onUpdateChip({ alias: event.target.value })}
        />
      ) : (
        <input
          className='form-control mb-3'
          value={selectedChip.alias || ''}
          onChange={(event) => onUpdateChip({ alias: event.target.value })}
          placeholder={t('rc_config_alias_placeholder')}
        />
      )}

      {selectedChip.zone === 'metrics' && Dropdown && (
        <div className='mt-3'>
          <label className='form-label'>{t('rc_config_aggregation_label')}</label>
          <Dropdown
            options={aggregationOptions}
            value={selectedAggregation || null}
            onChange={(option) =>
              onUpdateChip({
                aggregation: option?.value || selectedChip.aggregation,
              })
            }
            placeholder={t('rc_config_aggregation_placeholder')}
            width='100%'
          />
        </div>
      )}

      {selectedChip.zone === 'filters' && (
        <div className='mt-3'>
          {Dropdown && (
            <>
              <label className='form-label'>{t('rc_config_operator_label')}</label>
              <Dropdown
                options={filterOptions}
                value={selectedFilter || null}
                onChange={(option) =>
                  onUpdateChip({
                    operator: option?.value || filterOptions[0]?.value || 'eq',
                    values: [],
                  })
                }
                placeholder={t('rc_config_operator_placeholder')}
                width='100%'
              />
            </>
          )}

          <div className='mt-2'>
            <label className='form-label'>{t('rc_config_value_label')}</label>
            {selectedOperatorMeta?.requiresValue === false ? (
              <p className='caption color-gray-600 mb-0'>
                {t('rc_config_no_value_required')}
              </p>
            ) : selectedChip.supportsValueLookup ? (
              <>
                {ComboBox ? (
                  <ComboBox
                    options={valueOptions}
                    value={selectedValueOption || null}
                    onChange={(option) =>
                      onUpdateChip({
                        values: option?.value ? [option.value] : [],
                      })
                    }
                    onInputChange={(text) => setValueSearch(text || '')}
                    placeholder={t('rc_config_value_search_placeholder')}
                    width='100%'
                  />
                ) : Dropdown ? (
                  <Dropdown
                    options={valueOptions}
                    value={selectedValueOption || null}
                    onChange={(option) =>
                      onUpdateChip({
                        values: option?.value ? [option.value] : [],
                      })
                    }
                    placeholder={t('rc_config_value_select_placeholder')}
                    width='100%'
                  />
                ) : (
                  <select
                    className='form-select'
                    value={currentFirstValue}
                    onChange={(event) =>
                      onUpdateChip({
                        values: event.target.value ? [event.target.value] : [],
                      })
                    }
                  >
                    <option value=''>{t('rc_config_value_select_option')}</option>
                    {valueOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                )}
                {loadingValues && (
                  <p className='caption color-gray-600 mt-2 mb-0'>
                    {t('rc_config_loading_options')}
                  </p>
                )}
                {valueError && (
                  <p className='caption text-danger mt-2 mb-0'>{valueError}</p>
                )}
              </>
            ) : (
              <>
                {selectedOperatorMeta?.requiresValue !== false &&
                  (Input ? (
                    <Input
                      type={filterInputType}
                      placeholder={t('rc_config_filter_value_placeholder')}
                      value={currentFirstValue}
                      onChange={(event) =>
                        onUpdateChip({ values: [event.target.value] })
                      }
                    />
                  ) : (
                    <input
                      className='form-control'
                      type={filterInputType}
                      value={currentFirstValue}
                      onChange={(event) =>
                        onUpdateChip({ values: [event.target.value] })
                      }
                      placeholder={t('rc_config_filter_value_placeholder')}
                    />
                  ))}
                {selectedOperatorMeta?.requiresSecondValue && (
                  <div className='mt-2'>
                    {Input ? (
                      <Input
                        type={filterInputType}
                        placeholder={t('rc_config_second_value_placeholder')}
                        value={currentSecondValue}
                        onChange={(event) =>
                          onUpdateChip({
                            values: [currentFirstValue, event.target.value],
                          })
                        }
                      />
                    ) : (
                      <input
                        className='form-control'
                        type={filterInputType}
                        value={currentSecondValue}
                        onChange={(event) =>
                          onUpdateChip({
                            values: [currentFirstValue, event.target.value],
                          })
                        }
                        placeholder={t('rc_config_second_value_placeholder')}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {selectedChip.zone === 'sort' && Dropdown && (
        <div className='mt-3'>
          <label className='form-label'>{t('rc_config_direction_label')}</label>
          <Dropdown
            options={sortOptions}
            value={selectedSort || null}
            onChange={(option) =>
              onUpdateChip({ direction: option?.value || 'asc' })
            }
            placeholder={t('rc_config_direction_placeholder')}
            width='100%'
          />
        </div>
      )}
    </aside>
  );
};

export default ConfigPanel;
